import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { useFeatures } from '../components/Providers/FeatureProvider';
import { LlmEnum, llms } from '../Types/llm';
import { useIsMobile } from '../util/useIsMobile';
import { AnalyticsEvent, useAnalytics } from './Providers/AnalyticsProvider';

const LlmSelect = ({
  value,
  onLlmChange,
  mobileNavbarLlm,
}: {
  value: LlmEnum;
  onLlmChange: (llm: LlmEnum) => void;
  mobileNavbarLlm?: boolean;
}): JSX.Element => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [llm, setLLM] = useState<LlmEnum>(value || LlmEnum.GPT_41);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isMobile = useIsMobile();
  const features = useFeatures();
  const analytics = useAnalytics();

  useEffect(() => {
    setLLM(value || LlmEnum.GPT_41);
  }, [value]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (newValue?: LlmEnum) => {
    setAnchorEl(null);
    if (newValue) {
      onLlmChange(newValue);
    }
  };

  return (
    <Tooltip
      enterDelay={1000}
      open={tooltipOpen && !isMobile}
      title={
        <Typography variant="caption">
          The LLM selected will be used for all prompts and conversations
        </Typography>
      }
      placement="left"
      sx={{ height: '40px', width: '140px' }}
      id={'llm-select-description'}
    >
      {isMobile ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent={mobileNavbarLlm ? 'flex-end' : 'flex-start'}
          // width="100%"
          sx={{ width: '100%' }}
        >
          <Typography variant="body1" onClick={handleClick} style={{ cursor: 'pointer' }}>
            {llms[value].displayName}
          </Typography>
          <IconButton onClick={handleClick} size="small">
            {open ? <ArrowDropUp /> : <ArrowDropDown />}
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={() => handleClose()}
            PaperProps={{
              style: {
                width: '100%',
                maxWidth: '100%',
                left: '0px',
                right: '0px',
                marginTop: '18px',
                marginLeft: '16px',
              },
            }}
          >
            {/* Model descriptions largely copied from https://platform.openai.com/docs/models */}
            <MenuItem
              disabled
              style={{
                marginTop: '-10px',
                whiteSpace: 'normal',
                opacity: 1,
                fontSize: '14px',
                color: 'white',
                background: '#757575',
              }}
            >
              The LLM selected will be used for all prompts and conversations
            </MenuItem>
            {Object.entries(LlmEnum).map(([key, value]) => {
              const llmEnumValue = value as LlmEnum;
              const llm = llms[llmEnumValue];
              if (llm.hidden) {
                return null;
              }
              if (llm.featureFlag && !features[llm.featureFlag]) {
                return null;
              }
              return (
                <MenuItem
                  key={key}
                  value={llm.deploymentId}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}
                  onClick={() => handleClose(llmEnumValue)}
                >
                  {llm.displayName}
                  <Typography variant="body2" style={{ color: 'grey' }}>
                    {llm.description}
                  </Typography>
                  <Typography variant="caption1" style={{ color: 'grey', marginTop: '6px' }}>
                    {llm.subDescription}
                  </Typography>
                </MenuItem>
              );
            })}
          </Menu>
        </Box>
      ) : (
        <TextField
          id="llm-select"
          sx={{
            width: '100%',
            backgroundColor: 'white',
            '.MuiInputBase-root': {
              cursor: 'pointer',
            },
          }}
          value={llm}
          onMouseEnter={() => {
            if (!menuOpen) setTooltipOpen(true);
          }}
          onMouseLeave={() => setTooltipOpen(false)}
          onChange={(event) => {
            const newEnumValue = event.target.value as unknown as LlmEnum;
            const newValue = llms[newEnumValue].deploymentId;
            const oldValue = llms[llm].deploymentId;
            analytics.event(
              AnalyticsEvent.WM_NIGEL_LLM_CHANGE,
              {
                value: newValue,
              },
              {
                oldLLM: oldValue,
                newLLM: newValue,
              }
            );
            setLLM(newEnumValue);
            setTooltipOpen(false);
            onLlmChange(newEnumValue);
          }}
          select
          SelectProps={{
            onOpen: () => {
              setMenuOpen(true);
              setTooltipOpen(false);
            },
            onClose: () => {
              setMenuOpen(false);
              setTooltipOpen(false);
            },
            renderValue: (value) => {
              return (
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {llms[value as LlmEnum].displayName}
                </Typography>
              );
            },
            MenuProps: {
              PaperProps: { style: { marginTop: '10px', maxHeight: '375px' } },
              anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
              transformOrigin: { vertical: 'top', horizontal: 'right' },
              MenuListProps: { disablePadding: true },
            },
          }}
          size="small"
        >
          {Object.entries(LlmEnum).map(([key, value]) => {
            const llmEnumValue = value as LlmEnum;
            const llm = llms[llmEnumValue];
            if (llm.hidden) {
              return null;
            }
            if (llm.featureFlag && !features[llm.featureFlag]) {
              return null;
            }
            return (
              <MenuItem
                key={key}
                value={llm.deploymentId}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}
                onClick={() => handleClose(llmEnumValue)}
              >
                {llm.displayName}
                <Typography variant="body2" style={{ color: 'grey' }}>
                  {llm.description}
                </Typography>
                <Typography variant="caption1" style={{ color: 'grey', marginTop: '6px' }}>
                  {llm.subDescription}
                </Typography>
              </MenuItem>
            );
          })}
        </TextField>
      )}
    </Tooltip>
  );
};

export default LlmSelect;
